import { useCallback, useEffect, useState } from 'react';
import { fiscalDocuments, retryFiscal, type FiscalDocument } from './api';
import type { PaymentsKey, PaymentsTranslate } from './i18n';
import {
  Money,
  StatusBadge,
  Table,
  TableSkeleton,
  Checkbox,
  Pagination,
  usePagination,
} from '../../shell/kit';

export interface FiscalTabProps {
  readonly t: PaymentsTranslate;
  readonly onError: (message: string) => void;
}

export function FiscalTab({ t, onError }: FiscalTabProps) {
  const [rows, setRows] = useState<readonly FiscalDocument[]>([]);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | undefined>(undefined);

  const reload = useCallback(
    async (pending: boolean): Promise<void> => {
      setLoading(true);
      try {
        setRows(await fiscalDocuments(pending));
      } catch (err) {
        onError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [onError],
  );

  useEffect(() => {
    void reload(pendingOnly);
  }, [reload, pendingOnly]);

  const queued = rows.filter((r) => r.transmittedAt === null).length;

  // Скелет вместо строчки «Загрузка…»: держит высоту будущей таблицы, поэтому
  // страница не подпрыгивает в момент прихода данных.
  // Чеков за смену набирается под сотню: показываем страницами.
  const paged = usePagination(rows);

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-ink">{t('fiscal.title')}</h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            <Checkbox checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
            {t('fiscal.queue')}
            {queued > 0 && <StatusBadge tone="warn">{queued}</StatusBadge>}
          </label>
          <button
            type="button"
            disabled={busy}
            className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
            onClick={() => {
              setBusy(true);
              retryFiscal()
                .then(async (r) => {
                  setResult(t('fiscal.retried', { ...r }));
                  await reload(pendingOnly);
                })
                .catch((err: Error) => onError(err.message))
                .finally(() => setBusy(false));
            }}
          >
            {t('fiscal.retry')}
          </button>
        </div>
      </header>

      {result && <p className="text-sm text-ok">{result}</p>}

      {loading ? (
            <TableSkeleton rows={6} columns={6} />
          ) : rows.length === 0 ? (
        <p className="text-ink-muted">{t('fiscal.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded border border-line bg-surface">
          <Table className="text-left">
            <thead className="bg-sunken text-xs uppercase text-ink-muted">
              <tr>
                <th className="px-3 py-2">{t('tx.date')}</th>
                <th className="px-3 py-2">{t('fiscal.kind')}</th>
                <th className="px-3 py-2">{t('tx.amount')}</th>
                <th className="px-3 py-2">{t('fiscal.number')}</th>
                <th className="px-3 py-2">{t('fiscal.ofd')}</th>
                <th className="px-3 py-2">{t('tx.status')}</th>
                <th className="px-3 py-2">{t('fiscal.attempts')}</th>
              </tr>
            </thead>
            <tbody>
              {paged.rows.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="px-3 py-2 text-xs">{new Date(row.createdAt).toLocaleString()}</td>
                  <td className="px-3 py-2">{t(`fiscal.kind.${row.kind}` as PaymentsKey)}</td>
                  <td className="px-3 py-2 font-medium">
                    <Money minor={row.amountMinor} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{row.fiscalNumber ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.ofdReceiptId ?? '—'}</td>
                  <td className="px-3 py-2">
                    {row.status === 'skipped' ? (
                      <StatusBadge>{t('fiscal.skipped')}</StatusBadge>
                    ) : row.transmittedAt ? (
                      <StatusBadge tone="ok">{t('fiscal.transmitted')}</StatusBadge>
                    ) : (
                      <StatusBadge tone="warn">
                        {row.offline ? t('fiscal.offline') : t('fiscal.pending')}
                      </StatusBadge>
                    )}
                    {row.lastError && (
                      <div className="mt-1 text-xs text-danger">{row.lastError}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">{row.attempts}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="px-3 pb-3">
            <Pagination paged={paged} />
          </div>
        </div>
      )}
    </section>
  );
}
